// Complete Fixed App.js with Trial Management Integration
import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { 
  Target, 
  RefreshCw, 
  Settings,
  BarChart3,
  TrendingUp,
  Users,
  Shield,
  Eye,
  EyeOff,
  Menu,
  X,
  Home,
  Database,
  Bell,
  User,
  LogOut,
  AlertTriangle,
  CreditCard,
  Crown,
  ChevronDown,
  ChevronRight,
  Moon,
  Sun,
  PlayCircle,
  ArrowLeft,
  Clock,
  LayoutDashboard,
  TrendingDown,
  History
} from 'lucide-react';

// Import existing components
import { ThemeProvider, useTheme } from './context/ThemeContext';

// --- EAGER imports -----------------------------------------------------------
// Kept in the main bundle because they're needed on first paint (landing page)
// or the auth flow, and they're small. Splitting these would only add a network
// round-trip to the critical path.
import LandingPage from './components/LandingPage';
import LegalPage from './components/LegalPage';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import OAuthCallback from './components/auth/OAuthCallback';
import ForgotPassword from './components/auth/ForgotPassword';
import PendingApprovalPage from './components/auth/PendingApprovalPage';
import AccountInactivePage from './components/auth/AccountInactivePage';
import RegistrationRejectedPage from './components/auth/RegistrationRejectedPage';
import UnauthorizedPage from './components/auth/UnauthorizedPage';
import SubscriptionSuccessPage from './components/auth/SubscriptionSuccessPage';
import ContactPage from './components/ContactPage';

import { AuthProvider, useAuth } from './context/AuthContext';
import { forceLogin } from './context/AuthContext';
import { TradingViewAuthProvider } from './components/TradingViewAuth';
import tunaLogo from './assets/tunasignalai_logo.png';

// Import trial management components
import TrialExpirationHandler from './components/TrialExpirationHandler';
import { useTrialStatus } from './hooks/useTrialStatus';

// Activity tracking v2: page views on every route change (incl. navigate()),
// 30s presence heartbeat, data-track click capture, batched events.
import { initTracking } from './utils/tracking';
import CookieConsent from './components/CookieConsent';

// --- LAZY route components (code splitting) ----------------------------------
// PERF: These are the heavy authed views. Statically importing them forced the
// entire ~2.2MB bundle onto the landing page and every first paint, which is
// the dominant cause of the slow StockWatch load on mobile. React.lazy lets CRA
// (webpack) split each into its own chunk that loads only when its route is
// visited — no build-flag changes needed, webpack does this automatically.
// These are declared AFTER all static imports on purpose: `const = lazy(...)`
// is a statement, and ESLint's import/first rule forbids imports below it.
// Each target module must have a `default` export (verified: TunaSignalAI,
// ScalpWatch, ScannerPage, AlertsPage, LearnPage, SubscriptionPage,
// AdminDashboard, SocialMediaHub, ProfileManagement all do).
const TunaSignalAI      = lazy(() => import('./components/TunaSignalAI'));
const ScalpWatch        = lazy(() => import('./components/ScalpWatch'));
const ScannerPage       = lazy(() => import('./components/ScannerPage'));
const AlertsPage        = lazy(() => import('./components/AlertsPage'));
const LearnPage         = lazy(() => import('./components/LearnPage'));
const ReportsPage       = lazy(() => import('./components/ReportsPage'));
const SubscriptionPage  = lazy(() => import('./components/SubscriptionPage'));
const AdminDashboard    = lazy(() => import('./components/admin/AdminDashboard'));
const SocialMediaHub    = lazy(() => import('./components/SocialMediaHub'));
const ProfileManagement = lazy(() => import('./components/profile/ProfileManagement'));
// LIVE WATCH (2026-10-15): the new Stock Watch + Alerts flow (one live list + shared
// ticker panel), public to all subscribers. Old Stock Watch / Alerts pages are unchanged.
const LiveWatch         = lazy(() => import('./components/LiveWatch'));
// SHORT WATCH: short / fade score page. Live for subscribers (2026-10-30): second in the nav,
// same gating as Live Watch (ProtectedRoute + FeatureRestriction); API checks admin or active subscription.
const ShortWatch        = lazy(() => import('./components/ShortWatch'));
const ReplayPage        = lazy(() => import('./components/ReplayPage'));

// Deep-link capture (browser extension context menu → /stockwatch?symbol=XXX).
// Read at MODULE LOAD, before React mounts or the auth gate runs, because the
// ProtectedRoute redirect chain (<Navigate to="/stockwatch" />) drops the query
// string. AppContent consumes this once currentUser is present, then nulls it.
let pendingDeepLinkSymbol = (() => {
  try {
    const raw = new URLSearchParams(window.location.search).get('symbol');
    if (!raw) return null;
    const cleaned = raw.trim().toUpperCase().slice(0, 24);
    return cleaned || null;
  } catch {
    return null;
  }
})();

// Trial Status Banner Component
const TrialStatusBanner = ({ timeRemaining, onUpgradeClick }) => {
  const { darkMode } = useTheme();
  
  return (
    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center">
          <Clock className="w-5 h-5 mr-2" />
          <span className="font-medium">
            Trial ends in {timeRemaining}
          </span>
        </div>
        
        <button
          onClick={onUpgradeClick}
          className="bg-white text-orange-600 px-4 py-1 rounded-full font-medium hover:bg-orange-50 transition-colors flex items-center"
        >
          <Crown className="w-4 h-4 mr-1" />
          Upgrade
        </button>
      </div>
    </div>
  );
};

// Feature Restriction Component
const FeatureRestriction = ({ feature, children, user }) => {
  const { darkMode } = useTheme();
  const { isTrialExpired } = useTrialStatus();
  
  // Define which features are restricted for expired trials
  const restrictedFeatures = [
    'advanced_signals',
    'premium_alerts', 
    'unlimited_monitoring',
    'technical_analysis'
  ];
  
  const isRestricted = isTrialExpired && restrictedFeatures.includes(feature);
  
  if (isRestricted) {
    return (
      <div className="relative">
        <div className="opacity-30 pointer-events-none">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-[#07131F] bg-opacity-75 rounded">
          <div className="text-center text-white p-4">
            <Crown className="w-12 h-12 mx-auto mb-2 text-yellow-400" />
            <h3 className="font-bold mb-2">Premium Feature</h3>
            <p className="text-sm mb-4">Your trial has expired. Upgrade to access this feature.</p>
            <button
              onClick={() => window.location.href = '/subscription'}
              className="bg-[#5AA9F0] text-[#07131F] font-semibold px-4 py-2 rounded hover:bg-[#8CC4F5]"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};

// Public Legal Page Component with consistent styling
const PublicLegalPage = () => {
  const { darkMode, setDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return <LegalPage darkMode={darkMode} onBack={handleBack} />;
};

// Enhanced Public Subscription Page Component
const PublicSubscriptionPage = () => {
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const isFromRegistration = location.search.includes('from=registration');

  return (
    <SubscriptionPage 
      darkMode={darkMode} 
      isFromRegistration={isFromRegistration}
      onBack={() => navigate(-1)}
    />
  );
};

// Throttle the ProtectedRoute subscription gate so it doesn't call
// /api/subscription/status on every single navigation. We cache only a
// CONFIRMED-ACTIVE result for a short window; anything else still verifies
// live (fail-closed). The backend interceptor remains the hard gate on every
// data API call, so a subscription that expires mid-window is still caught
// the moment the user hits any protected endpoint.
const SUBSCRIPTION_GATE_TTL_MS = 12 * 60 * 60 * 1000; // 12h => ~2x/day
let __subGateCache = { userId: null, validUntil: 0 };

// RECOVERY FIX: The gate cache is module-level and survives clearAuthState()
// (which only touches localStorage/sessionStorage). Expose an explicit reset so
// the /login?recover=1 flow can invalidate a stale positive gate, preventing a
// user stuck bouncing to /subscription from riding a cached "active" result.
const resetSubGateCache = () => {
  __subGateCache = { userId: null, validUntil: 0 };
};

// RECOVERY FIX: A single flaky /api/subscription/status response used to eject a
// fully paid-up user into the subscription funnel. Retry transient failures with
// backoff; never retry a definitive answer (401/403) since those are real.
const fetchSubscriptionWithRetry = async (api, attempts = 3) => {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await api.get('/api/subscription/status');
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      if (status === 401 || status === 403) throw err;
      if (i < attempts - 1) {
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, i)));
      }
    }
  }
  throw lastError;
};

// LOOP GUARD (2026-10-09): Activity Log showed a user bouncing /stockwatch <->
// /subscription ~1x/second for a minute: this gate said "inactive" while the
// subscription page sent them back as "active". If we redirect to /subscription
// 3+ times in 30s, stop and show the retry screen instead of looping.
const SUB_BOUNCE_KEY = 'subGateBounces';
const recordSubscriptionBounce = () => {
  try {
    const now = Date.now();
    const recent = JSON.parse(sessionStorage.getItem(SUB_BOUNCE_KEY) || '[]')
      .filter((t) => now - t < 30000);
    recent.push(now);
    sessionStorage.setItem(SUB_BOUNCE_KEY, JSON.stringify(recent));
    return recent.length;
  } catch {
    return 0;
  }
};

const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { currentUser, loading, api } = useAuth();
  const location = useLocation();
  const [subscriptionCheck, setSubscriptionCheck] = useState({
    loading: true,
    valid: false,
    redirect: null,
    serviceError: false
  });

  useEffect(() => {
    const validateAccess = async () => {
      // PATCH REFRESH-STAYS-ON-PAGE (2026-09-28)
      // ROOT CAUSE: on a hard refresh AuthContext is still hydrating (loading=true)
      //   when this effect first runs. The old code stored redirect:'/login' with
      //   loading:false; the next render (auth done) rendered <Navigate to="/login">
      //   BEFORE this effect could re-validate. /login then saw a logged-in user with
      //   no location.state.from and bounced to /stockwatch.
      // FIX: stay in the loading state until auth settles; only redirect to login
      //   once we KNOW there is no user.
      if (loading) {
        setSubscriptionCheck({ loading: true, valid: false, redirect: null, serviceError: false });
        return;
      }
      if (!currentUser) {
        setSubscriptionCheck({ loading: false, valid: false, redirect: '/login', serviceError: false });
        return;
      }

      // SECURITY FIX #2: Never grant privilege based on a username string.
      // currentUser is loaded from localStorage which any user can edit via DevTools.
      // Trust only the roles array from the server-signed JWT.
      if (currentUser.roles?.includes('admin')) {
        setSubscriptionCheck({ loading: false, valid: true, redirect: null, serviceError: false });
        return;
      }

      // Check subscription for non-admin users
      if (requireSubscription) {
        // Fast path: reuse a recently confirmed-active result for this user
        // to avoid hitting /api/subscription/status on every navigation.
        // Require a token to still be present so a cleared session can't ride
        // a stale positive.
        if (
          localStorage.getItem('access_token') &&
          __subGateCache.userId === currentUser.id &&
          Date.now() < __subGateCache.validUntil
        ) {
          setSubscriptionCheck({ loading: false, valid: true, redirect: null, serviceError: false });
          return;
        }

        try {
          console.log('Checking subscription status...');
          const response = await fetchSubscriptionWithRetry(api);

          // FIX (2026-10-08): the auth axios instance resolves 4xx, so an expired
          // token came back as a "successful" response with is_active=false and
          // sent the user to /subscription (stuck on "Verifying your trial...").
          if (response.status === 401) {
            // Hard reload to /login: an in-app <Navigate> would keep the stale
            // currentUser in memory and PublicRoute would bounce back here.
            resetSubGateCache();
            forceLogin('session_expired');
            return; // keep spinner while the browser navigates
          }
          if (response.status >= 400 && response.status !== 403) {
            setSubscriptionCheck({ loading: false, valid: false, redirect: null, serviceError: true });
            return;
          }

          const subData = response.data;

          console.log('Subscription data:', {
            has_subscription: subData.has_subscription,
            is_active: subData.is_active,
            status: subData.subscription?.status,
            expires_at: subData.subscription?.expires_at
          });

          // Check if subscription is CURRENTLY ACTIVE
          const isActive = subData.is_active === true;
          const hasExpired = subData.has_subscription && !isActive;

          if (!isActive) {
            if (recordSubscriptionBounce() >= 3) {
              console.error('Subscription redirect loop detected - showing retry screen');
              setSubscriptionCheck({ loading: false, valid: false, redirect: null, serviceError: true });
              return;
            }
            console.log('No active subscription, redirecting to subscription page');
            const params = new URLSearchParams({
              expired: hasExpired ? 'true' : 'false',
              from: 'protected_route'
            });
            
            setSubscriptionCheck({
              loading: false,
              valid: false,
              redirect: `/subscription?${params.toString()}`,
              serviceError: false
            });
            return;
          }

          // Subscription is active - allow access
          console.log('Active subscription confirmed');
          __subGateCache = {
            userId: currentUser.id,
            validUntil: Date.now() + SUBSCRIPTION_GATE_TTL_MS
          };
          setSubscriptionCheck({ loading: false, valid: true, redirect: null, serviceError: false });
        } catch (error) {
          console.error('Subscription check failed:', error);
          
          // CRITICAL FIX: Handle different error types gracefully
          const status = error.response?.status;
          
          if (status === 404) {
            // RECOVERY FIX: Still deny access (fail-closed), but do NOT dump the
            // user into the subscription funnel — a missing endpoint is our fault,
            // not an expired subscription. Show a recoverable error instead.
            console.error('Subscription endpoint not found - DENYING access (fail-closed)');
            console.error('Backend needs to implement /api/subscription/status endpoint');
            setSubscriptionCheck({
              loading: false,
              valid: false,
              redirect: null,
              serviceError: true
            });
          } else if (status === 401 || error.isAuthExpired) {
            // Unauthorized - redirect to login (interceptor already cleared tokens)
            console.log('Unauthorized - redirecting to login');
            resetSubGateCache();
            forceLogin('session_expired'); // hard reload; keep spinner meanwhile
          } else if (status === 403) {
            // Subscription expired - redirect to subscription page
            console.log('Subscription expired (403)');
            setSubscriptionCheck({
              loading: false,
              valid: false,
              redirect: '/subscription?expired=true&from=auth_check',
              serviceError: false
            });
          } else {
            // RECOVERY FIX: Deny access, but surface a retryable error rather than
            // redirecting to /subscription. This was the entry point of the loop
            // that trapped active subscribers.
            console.error('Subscription check failed with unknown error - DENYING access (fail-closed)');
            console.error('Error details:', error.message);
            setSubscriptionCheck({
              loading: false,
              valid: false,
              redirect: null,
              serviceError: true
            });
          }
        }
      } else {
        // No subscription required
        setSubscriptionCheck({ loading: false, valid: true, redirect: null, serviceError: false });
      }
    };

    validateAccess();
  }, [currentUser, loading, requireSubscription, api]);

  // Show loading
  if (loading || subscriptionCheck.loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // RECOVERY FIX: We couldn't verify the subscription due to an infrastructure
  // problem. Never show the paywall here — the subscription itself is unchanged.
  // Offer a retry, plus a self-service escape hatch that clears stale auth state.
  if (subscriptionCheck.serviceError) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">We couldn't verify your subscription</h2>
        <p className="mb-6 max-w-md text-slate-600 dark:text-slate-400">
          This is a temporary problem on our end — your subscription has not changed.
          Please try again in a moment.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F] font-semibold rounded-lg transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => { window.location.href = '/login?recover=1'; }}
            className="px-4 py-2 border border-slate-300 dark:border-[#28445F] rounded-lg hover:bg-slate-100 dark:hover:bg-[#0D1E2E] transition-colors"
          >
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  // Handle redirects
  if (!currentUser || subscriptionCheck.redirect) {
    const target = subscriptionCheck.redirect || '/login';
    // Carry the current page so PublicRoute(/login) returns here, not /stockwatch
    return (
      <Navigate
        to={target}
        replace
        state={target.startsWith('/login') ? { from: location } : undefined}
      />
    );
  }

  if (!subscriptionCheck.valid) {
    return <Navigate to="/subscription?expired=true" replace />;
  }

  return children;
};

const handleLogout = () => {
  // Clear all auth data
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token'); 
  localStorage.removeItem('currentUser');
  localStorage.removeItem('subscriptionInfo');
  
  // Clear session storage
  sessionStorage.clear();
  
  // Redirect to home page
  window.location.href = '/?from_logout=true';
};

const AdminRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  if (!currentUser.roles?.includes('admin')) {
    return <Navigate to="/unauthorized" replace />;
  }
  
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

// FIXED: Complete rewrite of PublicRoute to fix account-inactive issue
const PublicRoute = ({ children }) => {
  const { currentUser, loading, clearAuthState } = useAuth();
  const location = useLocation();

  // RECOVERY FIX: Normalize trailing slashes so '/login//' behaves exactly like
  // '/login'. Users had been relying on '/login//' as an accidental bypass,
  // because the exact-match check below silently failed for it.
  const currentPath = location.pathname.replace(/\/+$/, '') || '/';

  // RECOVERY FIX: '/login?recover=1' force-clears stale auth state instead of
  // bouncing an apparently-logged-in user back into the app. This is the
  // supported replacement for the '/login//' bypass, and is safe to send to a
  // user over email/support chat.
  const wantsRecovery = new URLSearchParams(location.search).get('recover') === '1';

  useEffect(() => {
    if (wantsRecovery) {
      console.warn('Recovery mode requested - clearing stale auth + gate state');
      // Invalidate the module-level subscription gate cache so a stale positive
      // can't immediately bounce the user back toward /subscription.
      resetSubGateCache();
      // Drop any lingering Authorization header so the one-render window before
      // clearAuthState settles can't attach an old token to an in-flight call.
      try {
        if (window.axios?.defaults?.headers?.common?.Authorization) {
          delete window.axios.defaults.headers.common['Authorization'];
        }
      } catch (e) {
        /* non-fatal */
      }
      if (currentUser) {
        clearAuthState();
      }
    }
  }, [wantsRecovery, currentUser, clearAuthState]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // In recovery mode always render the login form, skipping every redirect below.
  if (wantsRecovery) {
    return children;
  }

  // CRITICAL FIX: Always allow access to login page regardless of subscription status
  const alwaysAccessiblePaths = ['/', '/login', '/register', '/forgot-password', '/legal', '/contact', '/learn', '/subscription'];
  
  if (alwaysAccessiblePaths.includes(currentPath)) {
    // For login page, check if user is already logged in and approved
    if (currentPath === '/login' && currentUser) {
      // ENHANCED: Check approval status but ignore subscription status for login
      if (currentUser.approval_status === 'rejected') {
        return <Navigate to="/registration-rejected" replace />;
      }
      
      // SECURITY FIX #2: Trust only roles, never a username string.
      const isAdmin = currentUser.roles?.includes('admin');
      
      if (isAdmin) {
        // Admin is already logged in, redirect to Live Watch (main page)
        return <Navigate to="/live-watch" replace />;
      }
      
      // For non-admin approved users, check where they came from
      const isApproved = currentUser.is_approved || 
                        currentUser.approval_status === 'approved';
      
      if (isApproved) {
        // SECURITY FIX #6: Validate the redirect path before using it.
        // An attacker could craft location.state.from to redirect post-login
        // to an unexpected internal route or a protocol-relative URL.
        const rawFrom = location.state?.from?.pathname;
        const safePath =
          rawFrom &&
          typeof rawFrom === 'string' &&
          rawFrom.startsWith('/') &&
          !rawFrom.startsWith('//')
            ? rawFrom
            : '/live-watch';
        return <Navigate to={safePath} replace />;
      }
      
      if (currentUser.approval_status === 'pending') {
        return <Navigate to="/pending-approval" replace />;
      }
    }
    
    // Allow access to public pages
    return children;
  }
  
  // No user logged in - allow access to public routes
  if (!currentUser) {
    return children;
  }
  
  // Handle authentication pages when user is already logged in
  if (['/register', '/forgot-password'].includes(currentPath)) {
    // Check user status and redirect appropriately
    if (currentUser.approval_status === 'pending') {
      return <Navigate to="/pending-approval" replace />;
    }
    
    if (currentUser.approval_status === 'rejected') {
      return <Navigate to="/registration-rejected" replace />;
    }
    
    // Check if user is approved
    const isApproved = currentUser.is_approved || 
                      currentUser.approval_status === 'approved' ||
                      // SECURITY FIX #2: Use roles, not username string
                      currentUser.roles?.includes('admin');
    
    if (isApproved) {
      // SECURITY FIX #6: Validate redirect path (same rule as login redirect above).
      const rawFrom = location.state?.from?.pathname;
      const safePath =
        rawFrom &&
        typeof rawFrom === 'string' &&
        rawFrom.startsWith('/') &&
        !rawFrom.startsWith('//')
          ? rawFrom
          : '/live-watch';
      return <Navigate to={safePath} replace />;
    }
    
    // User with unclear status - check if explicitly inactive
    if (currentUser.is_active === false) {
      return <Navigate to="/account-inactive" replace />;
    }
    
    // If we can't determine status, clear potentially corrupted data
    if (!currentUser.approval_status) {
      console.warn('User has incomplete status data, clearing auth...');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.reload();
      return children;
    }
  }
  
  // For any other public routes, allow access
  return children;
};

const PendingApprovalRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <div className="ml-4 text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  // If user is now approved, redirect to Live Watch (main page)
  if (currentUser.approval_status === 'approved' || currentUser.is_approved) {
    return <Navigate to="/live-watch" replace />;
  }
  
  if (currentUser.approval_status === 'rejected') {
    return <Navigate to="/registration-rejected" replace />;
  }
  
  // User is pending - show the pending approval page
  if (currentUser.approval_status === 'pending') {
    return children;
  }
  
  return <Navigate to="/account-inactive" replace />;
};

// Navigation Component — 2026-10-05 redesign
// Desktop (lg+): a permanent, see-through icon rail (64px). No solid panel, no
// slide-out on hover; each icon has a tooltip, the active page gets a sky-blue
// accent bar. The account avatar sits at the bottom and opens a small card
// (profile, theme, sign out). Mobile (<lg): same items with labels in a
// slide-in drawer opened from MobileHeader's menu button.
const Navigation = ({
  currentView,
  setCurrentView,
  darkMode,
  setDarkMode,
  user,
  onLogout,
  unreadAlertsCount,
  isOpen,
  setIsOpen,
}) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const sidebarRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = user?.roles?.includes('admin');
  const navItems = [
    { id: 'workspace', label: 'Live Watch', icon: LayoutDashboard },
    { id: 'short_watch', label: 'Short Watch', icon: TrendingDown },
    { id: 'replay', label: 'Replay', icon: History },
    { id: 'enhanced', label: 'Stock Watch', icon: Target },
    { id: 'alerts', label: 'Alerts', icon: Bell, badge: unreadAlertsCount > 0 ? unreadAlertsCount : null },
    ...(isAdmin ? [
      { id: 'admin', label: 'Admin Dashboard', icon: Users, isAdmin: true },
    ] : []),
  ];

  const displayName = user ? (user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : (user.username || user.email || 'Account')) : '';
  const initials = (displayName || '?').split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

  // mobile drawer: close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target) && isOpen && window.innerWidth < 1024) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen, setIsOpen]);

  // account card: close on outside click / Escape
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onDown = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [userMenuOpen]);

  const handleViewChange = (viewId) => {
    setCurrentView(viewId);
    const routes = {
      alerts_subscription: '/alerts/subscription', admin: '/admin', social_media: '/social-media',
      enhanced: '/stockwatch', scalp_watch: '/scalp-watch', scanner: '/scanner', alerts: '/alerts',
      learn: '/learn', profile: '/profile', workspace: '/live-watch', short_watch: '/short-watch', replay: '/replay',
    };
    if (routes[viewId]) navigate(routes[viewId]);
    setUserMenuOpen(false);
    if (window.innerWidth < 1024) setIsOpen(false);
  };

  const c = darkMode
    ? { ink: 'text-[#DCE6F0]', idle: 'text-slate-400 hover:text-[#DCE6F0] hover:bg-white/5', on: 'text-[#5AA9F0] bg-[#5AA9F0]/10', line: 'border-[#1C3348]', tip: 'bg-[#0D1E2E] border-[#28445F] text-[#DCE6F0]', card: 'bg-[#0D1E2E] border-[#1C3348]', cardItem: 'text-slate-300 hover:bg-[#13283C] hover:text-white', mute: 'text-slate-400', drawer: 'bg-[#07131F]' }
    : { ink: 'text-slate-900', idle: 'text-slate-500 hover:text-slate-900 hover:bg-slate-900/5', on: 'text-[#1F6FB5] bg-[#5AA9F0]/15', line: 'border-slate-200', tip: 'bg-slate-900 border-slate-900 text-white', card: 'bg-white border-slate-200', cardItem: 'text-slate-700 hover:bg-slate-100', mute: 'text-slate-500', drawer: 'bg-white' };

  // one button, two presentations: label row in the mobile drawer, icon + tooltip on the desktop rail
  const RailButton = ({ label, Icon, badge, active, onClick }) => (
    <button
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`group relative flex items-center gap-3 h-10 w-full px-3 lg:w-10 lg:px-0 lg:mx-auto lg:justify-center rounded-lg transition-colors ${active ? c.on : c.idle}`}
    >
      {active && <span className="hidden lg:block absolute -left-3 top-2 bottom-2 w-[3px] rounded-r bg-[#5AA9F0]" aria-hidden="true" />}
      <Icon size={18} strokeWidth={active ? 2.25 : 1.75} />
      <span className="lg:hidden text-sm font-medium">{label}</span>
      {badge && (
        <>
          <span className="lg:hidden ml-auto text-[11px] font-semibold px-1.5 rounded-full bg-[#F0414F] text-white">{badge}</span>
          <span className="hidden lg:flex absolute top-1 right-1 min-w-[16px] h-4 px-1 items-center justify-center rounded-full bg-[#F0414F] text-white text-[9px] font-bold leading-none">{badge > 9 ? '9+' : badge}</span>
        </>
      )}
      {/* desktop tooltip */}
      <span className={`hidden lg:block pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-medium opacity-0 translate-x-[-4px] transition-all duration-150 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 z-[80] ${c.tip}`}>
        {label}{badge ? ` (${badge} new)` : ''}
      </span>
    </button>
  );

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-[55] bg-black/50 lg:hidden" onClick={() => setIsOpen(false)} />}

      <aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-[60] w-64 flex flex-col border-r transition-transform duration-200 ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:sticky lg:top-0 lg:h-screen lg:w-16 lg:translate-x-0 lg:transition-none lg:z-[60] lg:bg-transparent ${c.drawer} ${c.line}`}
      >
        {/* top: logo (home) */}
        <div className="flex items-center justify-between h-14 px-4 lg:px-0 lg:justify-center shrink-0">
          <button onClick={() => navigate('/')} aria-label="TunaSignal AI home" className="group relative flex items-center gap-2">
            <img src={tunaLogo} alt="" className="w-7 h-7" />
            <span className={`lg:hidden font-semibold ${c.ink}`}>TunaSignal AI</span>
            <span className={`hidden lg:block pointer-events-none absolute left-full ml-4 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity z-[80] ${c.tip}`}>Home</span>
          </button>
          <button onClick={() => setIsOpen(false)} aria-label="Close menu" className={`lg:hidden p-1.5 rounded-lg ${c.idle}`}><X size={18} /></button>
        </div>

        {/* main items */}
        <nav className="flex-1 px-3 py-2 space-y-1.5" aria-label="Main">
          {navItems.map((item, i) => (
            <React.Fragment key={item.id}>
              {item.isAdmin && (i === 0 || !navItems[i - 1].isAdmin) && <div className={`my-2 mx-1 lg:mx-2 border-t ${c.line}`} />}
              <RailButton label={item.label} Icon={item.icon} badge={item.badge} active={currentView === item.id} onClick={() => handleViewChange(item.id)} />
            </React.Fragment>
          ))}
        </nav>

        {/* bottom: learn + account */}
        {user && (
          <div className="px-3 pb-3 pt-2 space-y-1.5 shrink-0">
            <RailButton label="Learn" Icon={PlayCircle} active={currentView === 'learn'} onClick={() => handleViewChange('learn')} />
            <div className={`mx-1 lg:mx-2 border-t ${c.line}`} />
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-haspopup="menu" aria-expanded={userMenuOpen} aria-label={`Account: ${displayName}`}
                className={`group relative flex items-center gap-3 w-full p-1.5 lg:p-0 lg:w-10 lg:h-10 lg:mx-auto lg:justify-center rounded-lg transition-colors ${userMenuOpen ? c.on : c.idle}`}
              >
                <span className="relative flex items-center justify-center w-8 h-8 rounded-full text-[12px] font-semibold text-[#07131F] shrink-0"
                  style={{ background: 'linear-gradient(135deg, #8CC4F5, #5AA9F0)' }}>
                  {initials}
                  <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 ${darkMode ? 'border-[#07131F]' : 'border-white'}`} aria-hidden="true" />
                </span>
                <span className="lg:hidden min-w-0 text-left">
                  <span className={`block text-sm font-medium truncate ${c.ink}`}>{displayName}</span>
                  {user.email && <span className={`block text-xs truncate ${c.mute}`}>{user.email}</span>}
                </span>
                {!userMenuOpen && <span className={`hidden lg:block pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity z-[80] ${c.tip}`}>{displayName}</span>}
              </button>

              {userMenuOpen && (
                <div role="menu" className={`absolute z-[90] bottom-full mb-2 left-0 right-0 lg:right-auto lg:bottom-0 lg:mb-0 lg:left-full lg:ml-3 lg:w-64 rounded-xl border shadow-2xl shadow-black/40 overflow-hidden ${c.card}`}>
                  <div className={`flex items-center gap-3 px-4 py-3 border-b ${c.line}`}>
                    <span className="flex items-center justify-center w-9 h-9 rounded-full text-[13px] font-semibold text-[#07131F] shrink-0" style={{ background: 'linear-gradient(135deg, #8CC4F5, #5AA9F0)' }}>{initials}</span>
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold truncate ${c.ink}`}>{displayName}</div>
                      {user.email && <div className={`text-xs truncate ${c.mute}`}>{user.email}</div>}
                      {isAdmin && <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-px rounded bg-[#5AA9F0]/15 text-[#5AA9F0]">Admin</span>}
                    </div>
                  </div>
                  <div className="p-1.5">
                    <button role="menuitem" onClick={() => handleViewChange('profile')} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${c.cardItem}`}>
                      <User className="w-4 h-4" /> Profile settings
                    </button>
                    {setDarkMode && (
                      <button role="menuitem" onClick={() => setDarkMode(!darkMode)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${c.cardItem}`}>
                        {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />} {darkMode ? 'Light mode' : 'Dark mode'}
                      </button>
                    )}
                  </div>
                  <div className={`p-1.5 border-t ${c.line}`}>
                    <button role="menuitem" onClick={() => { setUserMenuOpen(false); onLogout(); }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'text-[#FF7A85] hover:bg-[#F0414F]/10' : 'text-red-600 hover:bg-red-50'}`}>
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

// Mobile Header Component - Shows "X by TunaSignal AI" for main views
const MobileHeader = ({ 
  currentView, 
  darkMode, 
  setDarkMode, 
  user, 
  unreadAlertsCount, 
  isOpen, 
  setIsOpen 
}) => {
  const navigate = useNavigate();
  // Get view title and determine if it needs "by TunaSignal AI" branding
  const getViewConfig = () => {
    switch (currentView) {
      case 'enhanced': 
        return { title: 'Stock Watch', showBranding: true };
      case 'scalp_watch': 
        return { title: 'ScalpWatch', showBranding: true };
      case 'scanner': 
        return { title: 'Signal Scanner', showBranding: true };
      case 'alerts': 
        return { title: 'Alerts', showBranding: false };
      case 'alerts_subscription': 
        return { title: 'Alert Subscription', showBranding: false };
      case 'learn': 
        return { title: 'Learn', showBranding: false };
      case 'admin': 
        return { title: 'Admin Dashboard', showBranding: false };
      case 'social_media': 
        return { title: 'Social Media Hub', showBranding: false };
      case 'profile': 
        return { title: 'Profile', showBranding: false };
      case 'short_watch':
        return { title: 'Short Watch', showBranding: false };
      case 'replay':
        return { title: 'Replay', showBranding: false };
      case 'workspace':
        return { title: 'Live Watch', showBranding: true };
      default: 
        return { title: 'TunaSignal AI', showBranding: false };
    }
  };

  const viewConfig = getViewConfig();

  return (
    <div className={`lg:hidden sticky top-0 z-30 ${
      darkMode ? 'bg-[#0D1E2E] border-[#1C3348]' : 'bg-white border-slate-200'
    } border-b shadow-sm`}>
      <div className="flex items-center justify-between h-16 px-4">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`p-2 rounded-lg ${
              darkMode 
                ? 'hover:bg-[#13283C] text-slate-300' 
                : 'hover:bg-slate-100 text-slate-600'
            }`}
          >
            <Menu size={20} />
          </button>
          
          <div 
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('/')}
            title="Go to Home Page"
          >
            <img src={tunaLogo} alt="TunaSignal AI" className="w-6 h-6" />
            {viewConfig.showBranding ? (
              // Stock Watch / ScalpWatch with "by TunaSignal AI" branding
              <div className="flex items-baseline space-x-1">
                <span className={`font-bold text-base ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {viewConfig.title}
                </span>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  by
                </span>
                <span className={`font-semibold text-xs ${darkMode ? 'text-[#5AA9F0]' : 'text-blue-600'}`}>
                  TunaSignal AI
                </span>
              </div>
            ) : (
              // Standard view title (no branding suffix)
              <span className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {viewConfig.title}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {currentView === 'alerts' && unreadAlertsCount > 0 && (
            <div className="relative">
              <Bell className={`w-5 h-5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`} />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {unreadAlertsCount > 9 ? '9+' : unreadAlertsCount}
              </span>
            </div>
          )}
          
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2 rounded-lg ${
              darkMode
                ? 'hover:bg-[#13283C] text-yellow-400'
                : 'hover:bg-slate-100 text-slate-600'
            }`}
          >
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// Map a pathname to its AppContent view id
const pathToView = (path) => {
  if (path === '/alerts/subscription') return 'alerts_subscription';
  if (path === '/alerts') return 'alerts';
  if (path === '/scalp-watch') return 'scalp_watch';
  if (path === '/scanner') return 'scanner';
  if (path === '/admin' || path.startsWith('/admin/')) return 'admin';
  if (path === '/social-media') return 'social_media';
  if (path === '/short-watch') return 'short_watch';
  if (path === '/replay') return 'replay';
  if (path === '/profile') return 'profile';
  if (path === '/learn') return 'learn';
  if (path === '/stockwatch' || path === '/enhanced') return 'enhanced';
  // Live Watch is the main page (default view)
  return 'workspace';
};

// App Content Component with Trial Management
const AppContent = () => {
  const { currentUser, logout, loading, error, updateProfile } = useAuth();
  const { darkMode, setDarkMode } = useTheme();
  // Initialise from the URL so a refresh on /alerts never mounts StockWatch first
  const [currentView, setCurrentView] = useState(() => pathToView(window.location.pathname));
  const [unreadAlertsCount, setUnreadAlertsCount] = useState(0);
  const [selectedTicker, setSelectedTicker] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [notification, setNotification] = useState(null);
  const [chartModalOpen, setChartModalOpen] = useState(false);

  // Trial management integration
  const {
    trialStatus,
    isTrialExpired,
    isTrialExpiringSoon,
    isTrialActive,
    formattedTimeRemaining,
    showExpirationModal,
    setShowExpirationModal,
    refreshTrialStatus
  } = useTrialStatus();

  // Poll /api/subscriptions/current at most ~2x/day. Subscription state changes
  // rarely (expiry/upgrade), so re-fetching on every currentUser change is wasteful.
  // Cache result + timestamp; only hit the network if stale (>12h) or forced
  // (e.g. right after a payment/upgrade where we want fresh state immediately).
  const SUBSCRIPTION_REFRESH_MS = 12 * 60 * 60 * 1000; // 12h => 2x/day

  const fetchUserSubscription = useCallback(async (force = false) => {
    try {
      if (!force) {
        const cachedAt = parseInt(localStorage.getItem('subscriptionCheckedAt') || '0', 10);
        const cached = localStorage.getItem('subscriptionInfo');
        if (cached && Date.now() - cachedAt < SUBSCRIPTION_REFRESH_MS) {
          try {
            setUserSubscription(JSON.parse(cached));
            return;
          } catch {
            // fall through to network on parse error
          }
        }
      }

      const response = await fetch('/api/subscriptions/current', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = await response.json();
      setUserSubscription(data.subscription);

      if (data.subscription) {
        localStorage.setItem('subscriptionInfo', JSON.stringify(data.subscription));
      }
      localStorage.setItem('subscriptionCheckedAt', Date.now().toString());
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  }, [SUBSCRIPTION_REFRESH_MS]);

  const hasActiveSubscription = useCallback(() => {
    return userSubscription?.is_active || false;
  }, [userSubscription]);

  const checkFeatureAccess = useCallback((feature) => {
    if (!hasActiveSubscription()) return false;
    const features = userSubscription?.features || [];
    return features.includes(feature) || features.includes('full_access');
  }, [hasActiveSubscription, userSubscription]);

  // Handle upgrade completion
  const handleUpgradeComplete = () => {
    refreshTrialStatus();
    // Bypass the throttle — the user just changed their subscription.
    __subGateCache = { userId: null, validUntil: 0 }; // force gate re-check
    fetchUserSubscription(true);
    setShowExpirationModal(false);
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserSubscription();
    }
  }, [currentUser, fetchUserSubscription]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    const path = location.pathname;
    setCurrentView(pathToView(path));

    // Reset chart modal state when navigating away from Stock Watch
    if (path !== '/stockwatch') {
      setChartModalOpen(false);
    }
  }, [location.pathname]);

  // Deep-link: the browser extension's context menu opens
  // /stockwatch?symbol=XXX. The auth gate (ProtectedRoute) can redirect to
  // /login and back before AppContent stably mounts, and those <Navigate>
  // calls carry only the pathname — so by the time this component's
  // location.search is read, ?symbol= is already gone. We therefore grab the
  // symbol from pendingDeepLinkSymbol, which was captured at module load
  // (before React's auth cycle ran), and apply it once currentUser is present.
  useEffect(() => {
    if (!currentUser) return;              // wait until auth has settled
    if (!pendingDeepLinkSymbol) return;    // nothing captured, or already applied

    const symbol = pendingDeepLinkSymbol;
    pendingDeepLinkSymbol = null;          // consume it — apply only once

    setSelectedTicker(symbol);
    setCurrentView('enhanced');
    // Land on the stockwatch view if the redirect chain left us elsewhere.
    if (location.pathname !== '/stockwatch') {
      navigate('/stockwatch');
    }

    // Clean the address bar (in case the param survived) without a route change.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('symbol')) {
        url.searchParams.delete('symbol');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    } catch {
      /* ignore */
    }
  }, [currentUser, location.pathname, navigate]);

  // PERF: Prefetch the Profile chunk once the main view has settled, so opening
  // Profile doesn't wait on a network round-trip for its lazy JS chunk.
  useEffect(() => {
    const prefetch = () => { import('./components/profile/ProfileManagement').catch(() => {}); };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 5000 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(prefetch, 3000);
    return () => clearTimeout(t);
  }, []);

  const handleUnreadCountUpdate = useCallback((count) => {
    setUnreadAlertsCount(count);
  }, []);

  const handleNavigateToStock = useCallback((ticker) => {
    setSelectedTicker(ticker);
    setCurrentView('enhanced');
    navigate('/stockwatch');
  }, [navigate]);

  // Navigate to Alerts page with pre-set filters (from TunaSignalAI right-click context menu)
  const handleNavigateToAlerts = useCallback((ticker, alertType) => {
    setCurrentView('alerts');
    navigate('/alerts');
  }, [navigate]);

  const handleChartModalChange = useCallback((isOpen) => {
    setChartModalOpen(isOpen);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // ScalpWatch needs strict viewport height containment (no scrolling)
  const isScalpWatch = currentView === 'scalp_watch';

  return (
    <div className={`${isScalpWatch ? 'h-screen' : 'min-h-screen'} flex flex-col transition-colors ${
      darkMode ? 'bg-[#07131F]' : 'bg-slate-50'
    }`} style={isScalpWatch ? { height: '100vh', overflow: 'hidden', position: 'relative' } : undefined}>
      
      {/* Trial Status Banner - shown at top of app when trial is expiring */}
      {isTrialActive && isTrialExpiringSoon && (
        <TrialStatusBanner 
          timeRemaining={formattedTimeRemaining}
          onUpgradeClick={() => setShowExpirationModal(true)}
        />
      )}

      {/* Main App Layout */}
      <div className="flex flex-1 min-h-0">
        <Navigation 
          currentView={currentView}
          setCurrentView={setCurrentView}
          unreadAlertsCount={unreadAlertsCount}
          user={currentUser}
          darkMode={darkMode}
          onLogout={logout}
          setDarkMode={setDarkMode}
          isOpen={sidebarOpen}
          setIsOpen={setSidebarOpen}
          hasActiveSubscription={hasActiveSubscription()}
          chartModalOpen={chartModalOpen}
        />

        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <MobileHeader 
            currentView={currentView}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            user={currentUser}
            unreadAlertsCount={unreadAlertsCount}
            isOpen={sidebarOpen}
            setIsOpen={setSidebarOpen}
          />

          {notification && (
            <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-3 ${
              notification.type === 'success' 
                ? 'bg-emerald-500 text-white' 
                : notification.type === 'error'
                ? 'bg-red-500 text-white'
                : 'bg-[#5AA9F0] text-[#07131F] font-semibold'
            }`}>
              <div className="flex-1">{notification.message}</div>
              <button 
                onClick={() => setNotification(null)}
                className="text-white hover:text-slate-200"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <main className="flex-1 overflow-hidden relative min-h-0">
            {/* Trial Expired Overlay */}
            {isTrialExpired && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mx-4 mt-4">
                <p className="font-medium">Trial Expired</p>
                <p className="text-sm">
                  Your trial has expired. Upgrade to continue using premium features.
                </p>
                <button
                  onClick={() => setShowExpirationModal(true)}
                  className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  Upgrade Now
                </button>
              </div>
            )}

            {/* PERF: Suspense boundary for the lazily-loaded views below. While a
                view's chunk downloads, this spinner shows in place of the main
                content instead of a blank <main>. */}
            <Suspense fallback={
              <div className="flex justify-center items-center h-full w-full">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            }>
            {currentView === 'enhanced' && (
              <FeatureRestriction feature="advanced_signals" user={currentUser}>
                <TunaSignalAI 
                  darkMode={darkMode}
                  setDarkMode={setDarkMode}
                  selectedTicker={selectedTicker}
                  onTickerSelect={setSelectedTicker}
                  hasFullAccess={hasActiveSubscription()}
                  onUpgradeRequired={() => navigate('/subscription')}
                  onNavigateToAlerts={handleNavigateToAlerts}
                  onChartModalChange={handleChartModalChange}
                />
              </FeatureRestriction>
            )}

            {currentView === 'scalp_watch' && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                <FeatureRestriction feature="advanced_signals" user={currentUser}>
                  <ScalpWatch 
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                    hasFullAccess={hasActiveSubscription()}
                    onUpgradeRequired={() => navigate('/subscription')}
                  />
                </FeatureRestriction>
              </div>
            )}

            {/* LIVE WATCH: same subscription gating as Stock Watch */}
            {currentView === 'workspace' && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                <FeatureRestriction feature="advanced_signals" user={currentUser}>
                  <LiveWatch darkMode={darkMode} setDarkMode={setDarkMode} />
                </FeatureRestriction>
              </div>
            )}

            {currentView === 'scanner' && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                <FeatureRestriction feature="advanced_signals" user={currentUser}>
                  <ScannerPage
                    darkMode={darkMode}
                  />
                </FeatureRestriction>
              </div>
            )}
            
            {currentView === 'alerts' && (
              <FeatureRestriction feature="premium_alerts" user={currentUser}>
                <AlertsPage 
                  darkMode={darkMode}
                  setDarkMode={setDarkMode}
                  onUnreadCountChange={setUnreadAlertsCount}
                  onNavigateToStock={handleNavigateToStock}
                  hasFullAccess={hasActiveSubscription()}
                  onUpgradeRequired={() => navigate('/subscription')}
                />
              </FeatureRestriction>
            )}

            {currentView === 'learn' && (
              <LearnPage 
                darkMode={darkMode}
                setDarkMode={setDarkMode}
              />
            )}
            
            {currentView === 'profile' && (
              <div className="h-full overflow-y-auto">
                <ProfileManagement 
                  darkMode={darkMode}
                  updateProfile={updateProfile}
                  setDarkMode={setDarkMode}
                  onViewPlans={() => navigate('/subscription')}
                  currentSubscription={userSubscription}
                />
              </div>
            )}
            
            {currentView === 'admin' && (
              <div className="h-full overflow-y-auto">
                <AdminDashboard darkMode={darkMode} />
              </div>
            )}

            {/* SHORT WATCH: same subscription gating as Live Watch */}
            {currentView === 'short_watch' && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                <FeatureRestriction feature="advanced_signals" user={currentUser}>
                  <ShortWatch darkMode={darkMode} />
                </FeatureRestriction>
              </div>
            )}

            {/* REPLAY: same subscription gating as Live Watch */}
            {currentView === 'replay' && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                <FeatureRestriction feature="advanced_signals" user={currentUser}>
                  <ReplayPage darkMode={darkMode} />
                </FeatureRestriction>
              </div>
            )}

            {currentView === 'social_media' && (
              <SocialMediaHub darkMode={darkMode} />
            )}
            </Suspense>
          </main>
        </div>
      </div>

      {/* Trial Expiration Modal */}
      {showExpirationModal && (
        <TrialExpirationHandler
          user={currentUser}
          trialStatus={trialStatus}
          onClose={() => setShowExpirationModal(false)}
          onUpgrade={handleUpgradeComplete}
        />
      )}
    </div>
  );
};

// Login Page Component
const LoginPage = () => {
  const { darkMode } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-[#07131F]' : 'bg-slate-50'
    } flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center relative">
          <div className="flex justify-center">
            <div className="w-16 h-16 flex items-center justify-center">
              <img src={tunaLogo} alt="TunaSignal AI" className="w-16 h-16" />
            </div>
          </div>
          <h1 className={`mt-4 text-3xl font-bold ${
            darkMode ? 'text-white' : 'text-slate-900'
          }`}>
            TunaSignal AI
          </h1>
          <p className={`mt-2 ${
            darkMode ? 'text-slate-400' : 'text-slate-600'
          }`}>
            Sign in to your account
          </p>
        </div>
        <LoginForm />
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const { darkMode } = useTheme();
  
  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#07131F]' : 'bg-slate-50'} flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8`}>
      <RegisterForm />
    </div>
  );
};

// Main App Component with complete routing
const App = () => {
  // Once for the whole app (public pages included), not per AppContent mount.
  useEffect(() => { initTracking(); }, []);

  return (
    <ThemeProvider>
      <TradingViewAuthProvider>
        <AuthProvider>
          <Router>
            {/* PERF: Top-level Suspense catches the route-level lazy components
                (LearnPage on /learn, SubscriptionPage inside PublicSubscriptionPage).
                The inner Suspense inside AppContent handles the authed view switch;
                this one is the fallback for everything rendered directly as a route
                element. Landing/auth routes are eager, so they never hit it. */}
            <Suspense fallback={
              <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            }>
            <Routes>
              {/* Public Landing Page - No route guard needed */}
              <Route path="/" element={<LandingPage />} />

              {/* Public authentication routes */}
              <Route 
                path="/login" 
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/register" 
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                } 
              />

              {/* Public accessible pages */}
              <Route path="/subscription" element={<PublicSubscriptionPage />} />
              <Route path="/legal" element={<PublicLegalPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/learn" element={<LearnPage />} />
              {/* Public performance reports (Weekly + EOD). :slug is deep-linkable
                  and SEO-friendly; ReportsPage handles both list and detail. */}
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/reports/:slug" element={<ReportsPage />} />

              <Route 
                path="/forgot-password" 
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                } 
              />

              <Route 
                path="/reset-password/:token" 
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                } 
              />

              {/* OAuth Callback Route */}
              <Route path="/oauth-callback" element={<OAuthCallback />} />

              {/* Status pages */}
              <Route 
                path="/pending-approval" 
                element={
                  <PendingApprovalRoute>
                    <PendingApprovalPage />
                  </PendingApprovalRoute>
                } 
              />
              
              <Route path="/account-inactive" element={<AccountInactivePage />} />
              <Route path="/registration-rejected" element={<RegistrationRejectedPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Subscription Success Page */}
              <Route 
                path="/subscription-success" 
                element={
                  <PublicRoute>
                    <SubscriptionSuccessPage />
                  </PublicRoute>
                } 
              />

              {/* Protected routes */}
              <Route 
                path='/stockwatch' 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/scalp-watch" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/scanner" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/alerts/subscription" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/alerts" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />

              {/* Admin routes */}
              <Route 
                path="/admin" 
                element={
                  <AdminRoute>
                    <AppContent />
                  </AdminRoute>
                } 
              />

              <Route 
                path="/admin/*" 
                element={
                  <AdminRoute>
                    <AppContent />
                  </AdminRoute>
                } 
              />

              <Route 
                path="/live-watch" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />
              {/* old preview URL → new name */}
              <Route path="/workspace" element={<Navigate to="/live-watch" replace />} />

              {/* Short Watch: subscribers, same as Live Watch */}
              <Route 
                path="/short-watch" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />

              {/* Replay: subscribers, same as Live Watch */}
              <Route 
                path="/replay" 
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/social-media" 
                element={
                  <AdminRoute>
                    <AppContent />
                  </AdminRoute>
                } 
              />

              {/* Catch-all redirects */}
              {/* RECOVERY FIX: '/dashboard' was navigated to from SubscriptionPage
                  but never defined, so it fell through to the '*' catch-all and
                  dumped active subscribers on the landing page. */}
              <Route path="/dashboard" element={<Navigate to='/live-watch' replace />} />
              <Route path="/enhanced" element={<Navigate to='/stockwatch' replace />} />
              {/* SECURITY FIX #10: /debugtuna previously redirected to itself, causing
                  an infinite redirect loop (ERR_TOO_MANY_REDIRECTS). Removed. */}
              
              {/* 404 fallback - redirect to landing page */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            <CookieConsent />
          </Router>
        </AuthProvider>
      </TradingViewAuthProvider>
    </ThemeProvider>
  );
};

export default App;
